// OPS-1 (2026-05-29) — header notification bell + dropdown inbox.
// Operational, in-app, actionable: clicking an item navigates to the claim.
// Reads GET /me/notifications (stored event rows UNION derived standing
// conditions). Polls lightly; refreshes when `refreshKey` changes.
import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch, can } from "../api";
import { useNavigation } from "./nav/NavigationContext";

const ICON = {
  task_assigned: "📌", task_overdue: "⏰", task_due_soon: "📅",
  reserve_review_due: "💰", document_received: "📄",
  invoice_force_void: "🧾", email_failed: "✉️",
  profile_publish_req: "🌐", profile_published: "🌐", profile_rejected: "🌐",
  cred_expiring_60: "🪪", cred_expiring_30: "🪪", cred_expiring_7: "🪪", cred_expired: "🪪",
  list_item_proposed: "🏷️", list_item_approved: "🏷️", list_item_rejected: "🏷️",
  default: "🔔",
};

export default function NotificationBell({ onNavigateToClaim, onNavigateToBillReview, refreshKey }) {
  const [open, setOpen]   = useState(false);
  const [data, setData]   = useState({ items: [], unread_count: 0 });
  const wrapRef = useRef(null);
  const { navigate } = useNavigation();

  const load = useCallback(() => {
    apiFetch("/me/notifications").then(d => setData(d || { items: [], unread_count: 0 })).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 120000); // light poll every 2 min
    return () => clearInterval(t);
  }, [load]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const openPanel = () => {
    setOpen(o => !o);
    if (!open && data.unread_count > 0) {
      apiFetch("/notifications/read-all", { method: "POST" }).then(() => load()).catch(() => {});
    }
  };

  const clickItem = (n) => {
    setOpen(false);
    // OPS-3: a bill-review notification opens the claim's Bills tab focused on
    // that bill, not just the claim — "open the bill review context".
    const focus = n.ref_table === "bill_intake_items" ? { tab: "bills", billId: n.ref_id } : null;
    // GO_LIVE P3 (invariant #6 — deep-link to the OWNING surface): an invoice
    // notification (e.g. force-void) opens the Invoice Workspace for invoicing-
    // capable users; others fall through to the claim.
    if (n.ref_table === "fee_invoices" && n.ref_id && can("manage_invoicing")) {
      navigate({ page: "invoice", entityType: "invoice", entityId: n.ref_id, params: { invId: n.ref_id } });
      if (n.id) apiFetch(`/notifications/${n.id}`, { method: "PATCH", body: { read: true } }).catch(() => {});
      return;
    }
    // EMPLOYEE-PROFILE-1 (invariant #6): credential renewal warnings own the
    // Profile page; publish requests own Admin → Website Publish (approvers)
    // or fall back to the requester's own Profile.
    if (n.ref_table === "employee_credentials") {
      navigate({ page: "profile" });
      if (n.id) apiFetch(`/notifications/${n.id}`, { method: "PATCH", body: { read: true } }).catch(() => {});
      return;
    }
    if (n.ref_table === "website_publish_requests") {
      navigate({ page: can("manage_website_publish") ? "admin" : "profile" });
      if (n.id) apiFetch(`/notifications/${n.id}`, { method: "PATCH", body: { read: true } }).catch(() => {});
      return;
    }
    if (n.claim_id && onNavigateToClaim) onNavigateToClaim(n.claim_id, focus);
    // Scope E fix: a claimless document/bill no longer dead-ends — route it to
    // the Operations Document Review hub, focused on the item.
    else if (n.ref_table === "bill_intake_items" && n.ref_id && onNavigateToBillReview) onNavigateToBillReview(n.ref_id);
    if (n.id) apiFetch(`/notifications/${n.id}`, { method: "PATCH", body: { read: true } }).catch(() => {});
  };

  const dismiss = (e, n) => {
    e.stopPropagation();
    if (!n.id) return;
    apiFetch(`/notifications/${n.id}`, { method: "PATCH", body: { dismissed: true } }).then(load).catch(() => {});
  };

  const items = data.items || [];

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button onClick={openPanel} title="Notifications"
              style={{ position: "relative", background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: "#fff", padding: "4px 8px", lineHeight: 1 }}>
        🔔
        {data.unread_count > 0 && (
          <span style={{ position: "absolute", top: -2, right: 0, background: "#dc2626", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 700, padding: "1px 5px", minWidth: 16 }}>
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div style={panel}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", fontWeight: 700, fontSize: 13, display: "flex", justifyContent: "space-between" }}>
            <span>Notifications</span>
            <span style={{ color: "var(--text3)", fontWeight: 400 }}>{items.length}</span>
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {items.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>You're all caught up.</div>
            ) : items.map((n, i) => (
              <div key={n.id || `d${i}`} onClick={() => clickItem(n)} style={{ ...row, background: n.read_at ? "#fff" : "#f5f8ff" }}>
                <span style={{ fontSize: 16 }}>{ICON[n.event_type] || ICON.default}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: "var(--text1, #1a2438)", fontWeight: n.read_at ? 400 : 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.title}</div>
                  {n.claim_number && <div className="mono" style={{ fontSize: 10.5, color: "var(--blue)" }}>{n.claim_number}</div>}
                </div>
                {n.id && <button onClick={(e) => dismiss(e, n)} title="Dismiss" style={{ background: "transparent", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14 }}>×</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const panel = {
  position: "absolute", top: "calc(100% + 8px)", right: 0, width: 360, background: "#fff",
  border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 28px rgba(0,0,0,0.18)", zIndex: 1200, color: "var(--text1, #1a2438)",
};
const row = { display: "flex", gap: 10, alignItems: "center", padding: "9px 14px", borderBottom: "1px solid var(--border-light, #f0f0f0)", cursor: "pointer" };
